/**
 * devices/SessionRail.tsx — THE shared session list.
 *
 * ONE component serves both the blocking interstitial (`mode="choose"`) and the
 * permanent settings screen (`mode="manage"`). That is deliberate and it is the
 * load-bearing decision on this surface: the two screens show the same three
 * facts about the same three rows, and building them separately guarantees they
 * drift — different wording for "last active", different sort, one of them
 * quietly forgetting that `firstSeenAt` exists.
 *
 * ── THE RECENCY LADDER ────────────────────────────────────────────────────
 * The vertical GAP between two rows is proportional to the difference in
 * log-dormancy (`deviceCopy.ts::railGaps`). Two sessions used this morning sit
 * tight together; the office laptop nobody has touched in twelve days hangs
 * visibly far below them. You do not compare three timestamps — you see the
 * shape. That is a pre-attentive read of the exact fact the decision needs,
 * from data that is already on the wire.
 *
 * Row heights and touch targets are untouched by this: the time is carried
 * entirely in `marginTop`, so nothing about legibility or a 44px target moves.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { DeviceSummary } from '../../lib/deviceSession'
import { railGaps } from './deviceCopy'
import { ChoiceRow, FreeSlotRow, ManageRow, type Connector } from './SessionRow'

export interface SessionRailProps {
  devices: DeviceSummary[]
  mode: 'choose' | 'manage'
  still: boolean
  /** choose mode */
  selectedId?: string | null
  suggestedId?: string | null
  onSelect?: (deviceId: string) => void
  disabled?: boolean
  /** manage mode */
  freeSlots?: number
  busyId?: string | null
  notes?: Record<string, { tone: 'quiet' | 'danger'; text: string } | undefined>
  onRevoke?: (deviceId: string) => void
}

/** Gap above each row + which connector runs below it. */
function useLayout(devices: DeviceSummary[], freeSlots: number) {
  return useMemo(() => {
    const gaps = railGaps(devices)
    const total = devices.length + freeSlots
    const connectorFor = (i: number): Connector => {
      if (i >= total - 1) return 'none'
      // A segment that leads into an unused slot is dashed — the rail stops
      // being a record of sessions and starts being a record of capacity.
      return i >= devices.length - 1 && freeSlots > 0 ? 'dashed' : 'solid'
    }
    // The gap BELOW row i is the gap ABOVE row i+1; the connector has to span it.
    const gapBelow = (i: number): number => (i + 1 < devices.length ? gaps[i + 1] : i + 1 < total ? 14 : 0)
    return { gaps, connectorFor, gapBelow }
  }, [devices, freeSlots])
}

export function SessionRail(props: SessionRailProps) {
  const {
    devices, mode, still, selectedId = null, suggestedId = null, onSelect,
    disabled = false, freeSlots = 0, busyId = null, notes = {}, onRevoke,
  } = props
  const { gaps, connectorFor, gapBelow } = useLayout(devices, mode === 'manage' ? freeSlots : 0)
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const focused = useRef(false)

  // Focus the pre-selected row ONCE, when the list first has rows. One Enter
  // from done, which is the whole point of pre-selecting: the screen should not
  // demand a decision before it will let you act.
  //
  // Guarded by a ref rather than a dependency list because it must fire exactly
  // once — re-focusing whenever `selectedId` changes would yank focus away from
  // a pointer user mid-scroll, and arrow-key navigation moves focus itself.
  useEffect(() => {
    if (mode !== 'choose' || devices.length === 0 || focused.current) return
    focused.current = true
    const i = devices.findIndex((d) => d.deviceId === selectedId)
    const el = refs.current[i >= 0 ? i : 0]
    // A raw .focus() would scroll a long card; `preventScroll` keeps the heading
    // in view so the user reads what happened before they act on it.
    el?.focus({ preventScroll: true })
  }, [mode, devices, selectedId])

  const move = useCallback((from: number, delta: number | 'home' | 'end') => {
    const n = devices.length
    if (n === 0) return
    const to = delta === 'home' ? 0
      : delta === 'end' ? n - 1
        : (from + delta + n) % n
    onSelect?.(devices[to].deviceId)
    refs.current[to]?.focus()
  }, [devices, onSelect])

  const onKeyDown = useCallback((i: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'ArrowDown': case 'ArrowRight': e.preventDefault(); move(i, 1); break
      case 'ArrowUp': case 'ArrowLeft': e.preventDefault(); move(i, -1); break
      case 'Home': e.preventDefault(); move(i, 'home'); break
      case 'End': e.preventDefault(); move(i, 'end'); break
      default: break
    }
  }, [move])

  if (mode === 'choose') {
    return (
      <div role="radiogroup" aria-label="Choose a device to sign out" data-testid="device-session-rail">
        {devices.map((d, i) => (
          <ChoiceRow
            key={d.deviceId}
            device={d}
            index={i}
            selected={d.deviceId === selectedId}
            suggested={d.deviceId === suggestedId}
            disabled={disabled}
            connector={connectorFor(i)}
            gapAbove={gaps[i]}
            gapBelow={gapBelow(i)}
            still={still}
            onSelect={() => onSelect?.(d.deviceId)}
            onKeyDown={onKeyDown(i)}
            innerRef={(el) => { refs.current[i] = el }}
          />
        ))}
      </div>
    )
  }

  return (
    <ul data-testid="device-session-rail" className="list-none" aria-label="Your sessions">
      {/* Inside the <ul>, not around the whole rail: AnimatePresence only tracks
          its DIRECT children, so wrapping <SessionRail/> from outside would
          animate the list's arrival and drop every row exit on the floor. */}
      <AnimatePresence initial={false}>
        {devices.map((d, i) => (
          <ManageRow
            key={d.deviceId}
            device={d}
            index={i}
            connector={connectorFor(i)}
            gapAbove={gaps[i]}
            gapBelow={gapBelow(i)}
            still={still}
            busy={busyId === d.deviceId}
            note={notes[d.deviceId] ?? null}
            onRevoke={() => onRevoke?.(d.deviceId)}
            onRetry={() => onRevoke?.(d.deviceId)}
          />
        ))}
      </AnimatePresence>
      {Array.from({ length: freeSlots }).map((_, k) => {
        const i = devices.length + k
        return (
          <FreeSlotRow
            key={`slot-${k}`}
            index={i}
            gapAbove={14}
            connector={i < devices.length + freeSlots - 1 ? 'dashed' : 'none'}
            gapBelow={14}
            still={still}
          />
        )
      })}
    </ul>
  )
}
